import lodash from 'lodash'
import store from '@/store'
import { getRegionEnvsByCode } from '@/api/settings/request'
import { getLastHistoryByStatusWithoutResume } from '@/api/workPermit/util'
import { PermTypeMapKeys } from '@/utils/aclHelper'
import { EmailTypes, tenantProjectRouteNames, projectRouteNames, envParameterNames, standaloneWpRouteNames } from '@/utils/consts'
import { WORK_PERMIT_TYPES, WORK_PERMIT_STATUS } from '@/modules/workPermits/consts'
import i18n from '@/utils/i18n'
import dateUtil from '@/utils/dateUtil'
import notification from '@/utils/notification'
import { disableTenantContractorEdit } from '@/modules/workPermits/utils'
import { buildEmail, getUserEmails } from './util'
import { SEND_EMAIL } from './consts'
import { getRepresentativeEmails } from './tenantProject'
import {
  getObjectValue,
  getLink,
  generateDuration,
  getPermitBuildingCode,
  findSubmitterEmail,
  findMainContractorEmail,
  buildMetaData,
  buildExtendMetaDate,
  amendRejectExtensionMetaDate,
  getRelatedVendorEmail,
  findStandaloneSubmitterEmail,
} from './wpHelper'

function getRepresentativeEmailsExclude (excludedEmails) {
  excludedEmails = lodash.isArray(excludedEmails) ? excludedEmails : [excludedEmails]
  const getEmails = getRepresentativeEmails()
  return lodash.uniq(getEmails).filter(item => excludedEmails.indexOf(item) === -1)
}

function getDescSortedHistories (data, isAsc = false) {
  return lodash.chain(data).get('histories', []).cloneDeep().value().sort(dateUtil.dateSortFunc('createdAt', isAsc))
}

function getRouteNameForExternalByPermit (permit) {
  let routeName = ''
  if (permit.standalone) {
    routeName = permit.isTenantProject ? standaloneWpRouteNames.tenantStandaloneWp : standaloneWpRouteNames.vendorStandaloneWp
  } else {
    routeName = permit.isTenantProject ? tenantProjectRouteNames.tenantProjectDetail : projectRouteNames.vendorProjectDetail
  }
  return routeName
}

function formatDateTimeForChildPermit (workPermitsInProject) {
  workPermitsInProject.forEach(wp => {
    wp.self.duration = generateDuration(wp.self.startDate, wp.self.endDate)
    if (wp.hoistings.length) {
      wp.hoistings.forEach(hoisting => {
        hoisting.duration = generateDuration(hoisting.startDate, hoisting.endDate)
      })
    }
  })
}

async function getBccRecipients ({emailType, permit, buildingId, tenancyType}) {
  if (emailType === EmailTypes.workPermits.approveNotificationPP) {
    return getUserEmails(buildingId, tenancyType, PermTypeMapKeys.PROJECT_PERMIT_APPROVED_BCC_GROUP)
  }
  if (permit.type === WORK_PERMIT_TYPES.PROJECT) {
    return getUserEmails(buildingId, tenancyType, PermTypeMapKeys.PROJECT_PERMIT_BCC_GROUP)
  }
  if (permit.type === WORK_PERMIT_TYPES.WORK) {
    return getUserEmails(buildingId, tenancyType, PermTypeMapKeys.WORK_PERMIT_BCC_GROUP)
  }
  if (permit.type === WORK_PERMIT_TYPES.HOISTING) {
    return getHoistingBccRecipients(permit)
  }
  return []
}

async function getHoistingBccRecipients (permit) {
  const emails = await getRegionEnvsByCode(`mail.BMOICEmailsByBuilding.${getPermitBuildingCode(permit)}`)
  return emails || []
}

function noTriggerForExternal (permit) {
  return permit.type === WORK_PERMIT_TYPES.HOISTING || permit.type === WORK_PERMIT_TYPES.WORK
}

async function buildToContractorEmails ({permit, type, bccRequired}, amendMetaData) {
  let emails = []
  let { metaData, building, tenancy } = await buildMetaData(permit)
  if (permit.isTenantProject && disableTenantContractorEdit() && noTriggerForExternal(permit)) {
    return []
  }
  const emailTo = await findSubmitterEmail(permit)

  if (lodash.isEmpty(emailTo)) {
    notification.fireNotification(false, i18n.t('error.sendmail.404'))
    return []
  }
  if (permit.type === WORK_PERMIT_TYPES.PROJECT) {
    metaData.cc = getRepresentativeEmailsExclude(emailTo)
  }
  if (permit.type === WORK_PERMIT_TYPES.WORK || (permit.type === WORK_PERMIT_TYPES.HOISTING && permit.standalone)) {
    if (permit.standalone) {
      const { cc } = await findStandaloneSubmitterEmail(permit)
      metaData.cc = cc
    } else {
      addNonStandaloneCC(emailTo, metaData, permit)
    }
  }
  metaData.link = await getLink(permit, getRouteNameForExternalByPermit(permit), {}, envParameterNames.vendorEntry)
  metaData.bcc = bccRequired ? (await getBccRecipients({
    emailType: type,
    permit,
    buildingId: building.id,
    tenancyType: tenancy.type,
  })) : []
  if (typeof amendMetaData === 'function') {
    metaData = await amendMetaData(metaData)
  }
  buildEmail(emails, emailTo, type, metaData, SEND_EMAIL)
  return emails
}

function addNonStandaloneCC (emailTo, metaData, permit) {
  const subContractors = store.getters['projectContractors/currentContractors']
  let isSubUser = false

  if (subContractors) {
    isSubUser = subContractors.some(contractor => {
      return lodash.get(contractor, 'primaryContact.email') === emailTo
    })
  }
  if (isSubUser) {
    metaData.cc = findMainContractorEmail()
  } else {
    // get all related emails that submitted the work permit/hoisting in previous.
    metaData.cc = getRelatedVendorEmail(permit.histories)
    if (permit.hoistings) {
      let hoistingSubmitters = []
      permit.hoistings.forEach(hoisting => {
        const submitter = getRelatedVendorEmail(hoisting.histories)
        if (submitter) {
          hoistingSubmitters = [...hoistingSubmitters, ...submitter]
        }
      })
      metaData.cc = lodash.uniq([...metaData.cc, ...hoistingSubmitters]).filter(email => email !== emailTo)
    }
  }
}

async function applyWpToContractor (permit, type) {
  return buildToContractorEmails({permit, type, bccRequired: false})
}

async function applyHoistingToContractor (permit, type) {
  return buildToContractorEmails({permit, type, bccRequired: true})
}

async function applyExtendWPToContractor (permit, type) {
  return buildToContractorEmails({permit, type, bccRequired: true}, metaData => buildExtendMetaDate(permit, metaData))
}

async function approveExtendWP (permit, type) {
  return buildToContractorEmails({permit, type, bccRequired: true}, metaData => buildExtendMetaDate(permit, metaData))
}

async function rejectExtensionWP (permit, type) {
  return buildToContractorEmails({permit, type, bccRequired: true}, metaData => {
    return amendRejectExtensionMetaDate(permit, metaData)
  })
}

async function approveNotification (permit, type) {
  return buildToContractorEmails({
    permit,
    type,
    bccRequired: permit.type === WORK_PERMIT_TYPES.HOISTING || type === EmailTypes.workPermits.approveNotificationPP,
  })
}

async function rejected (permit, type) {
  return buildToContractorEmails({permit, type, bccRequired: true}, metaData => {
    let result = lodash.cloneDeep(metaData)
    result.remarks = resolveRejectRemarksFromPermit(permit)
    return result
  })
}

export function resolveRejectRemarksFromPermit (permit) {
  const rejectedHistory = getLastHistoryByStatusWithoutResume(permit.histories, WORK_PERMIT_STATUS.REJECTED)
  const externalRejectComments = getObjectValue(rejectedHistory, 'detail.externalComments')
  const normalized = String(externalRejectComments || '').replace(/\r\n/g, '\n')
  return normalized === '--' ? '' : normalized
}

async function suspend (permit, type) {
  return buildToContractorEmails({permit, type, bccRequired: true}, metaData => {
    return amendMetaDataForSuspendResumed(metaData, permit)
  })
}

async function resumed (permit, type) {
  return buildToContractorEmails({permit, type, bccRequired: true}, metaData => {
    return amendMetaDataForSuspendResumed(metaData, permit)
  })
}

async function amendMetaDataForSuspendResumed (metaData, permit) {
  let result = lodash.cloneDeep(metaData)
  result.additionalMsg = result.permit.hoistings.length || result.permit.workPermitsInProject.length
  result.onlyHoisting = result.permit.hoistings.length && !result.permit.workPermitsInProject.length
  result.hasHoisting = result.permit.workPermitsInProject.some(permit => permit.hoistings.length > 0) || result.permit.hoistings.length

  if (result.permit.workPermitsInProject.length) {
    formatDateTimeForChildPermit(result.permit.workPermitsInProject)
  }
  const orderHistories = getDescSortedHistories(permit, true)
  const suspendHistory = getLastHistoryByStatusWithoutResume(orderHistories, WORK_PERMIT_STATUS.SUSPENDED)
  result.remarks = getObjectValue(suspendHistory, 'detail.comments')
  if (permit.type === WORK_PERMIT_TYPES.PROJECT) {
    // add sub contractors
    // don't need to exclude main contractors as the email is sent to main contractor
    // duplicated emails will be handled properly at backend
    const currentContractors = store.getters['projectContractors/currentContractors']
    const contacts = currentContractors.map(contractor => contractor.primaryContact)
    const contractorEmails = contacts.filter(contact => contact.active).map(contact => contact.email)
    result.cc = [].concat(result.cc, contractorEmails)
  }
  return result
}

const workPermitsNotification = {
  applyWpToContractor,
  applyHoistingToContractor,
  applyExtendWPToContractor,

  approveNotificationHP: approveNotification,
  approveNotificationPP: approveNotification,
  approveNotificationWP: approveNotification,
  approveExtendWP,
  rejectExtensionWP,

  rejected,
  suspendPP: suspend,
  suspendWP: suspend,
  resumedPP: resumed,
  resumedWP: resumed,
}

export default workPermitsNotification
